import { type CreatePackageImageRequest, type PackageDetail } from '@utazzo/shared';
import { Trash2 } from 'lucide-react';
import React, { useState } from 'react';

import { useAddPackageImage, useDeletePackageImage } from '../use-packages';

import { FormSection } from '@/components/form-section';
import { MediaDropField } from '@/features/website-cms/components/MediaDropField';

interface GalleryEditorProps {
  /** Live (edit) mode: the existing package whose gallery is edited via the API. */
  pkg?: PackageDetail | null;
  /** Controlled (create) mode: staged gallery images held in the parent. */
  draftValue?: CreatePackageImageRequest[];
  /** Controlled (create) mode: provide to switch to in-memory editing (no API). */
  onDraftChange?: (items: CreatePackageImageRequest[]) => void;
}

export const GalleryEditor = ({
  pkg,
  draftValue,
  onDraftChange,
}: GalleryEditorProps): React.JSX.Element => {
  const controlled = onDraftChange !== undefined;
  const draftItems = draftValue ?? [];

  const add = useAddPackageImage(pkg?.id ?? '');
  const del = useDeletePackageImage(pkg?.id ?? '');
  const [newImageId, setNewImageId] = useState<string | null>(null);

  const handleAdd = (mediaId: string | null): void => {
    if (!mediaId) return;
    if (controlled) {
      onDraftChange([
        ...draftItems,
        { mediaAssetId: mediaId, altText: null, sortOrder: draftItems.length },
      ]);
      setNewImageId(null);
      return;
    }
    void add.mutateAsync({ mediaAssetId: mediaId }).then(() => setNewImageId(null));
  };

  const removeDraft = (i: number): void => {
    if (!onDraftChange) return;
    onDraftChange(
      draftItems.filter((_, idx) => idx !== i).map((img, idx) => ({ ...img, sortOrder: idx })),
    );
  };
  const removeLive = (imageId: string): void => {
    if (!window.confirm('Remove this image from the gallery?')) return;
    del.mutate(imageId);
  };

  const liveRows = pkg?.images ?? [];
  const isEmpty = controlled ? draftItems.length === 0 : liveRows.length === 0;

  return (
    <FormSection
      title="Gallery"
      description="Additional images shown in the package gallery. Add images one at a time."
    >
      <div className="space-y-6">
        {/* Add image picker */}
        <div>
          <p className="mb-2 text-xs font-medium text-(--fg-muted)">Add image</p>
          <MediaDropField
            value={newImageId}
            onChange={handleAdd}
            kind="image"
            folder="packages"
            variant="square"
            disabled={!controlled && add.isPending}
            label={undefined}
          />
        </div>

        {/* Existing / staged gallery images */}
        {isEmpty ? (
          <p className="rounded-lg border border-dashed border-zinc-300 bg-zinc-50/50 p-6 text-center text-xs text-zinc-500">
            No gallery images yet. Add one above.
          </p>
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {controlled
              ? draftItems.map((img, i) => (
                  <div key={`draft-${i}`} className="group relative">
                    <MediaDropField
                      value={img.mediaAssetId}
                      onChange={() => undefined}
                      kind="image"
                      folder="packages"
                      variant="square"
                      disabled
                      label={img.altText ?? undefined}
                    />
                    <button
                      type="button"
                      onClick={() => removeDraft(i)}
                      className="absolute top-1 right-1 z-10 inline-flex h-7 w-7 items-center justify-center rounded-md bg-black/60 text-white opacity-0 transition-opacity group-hover:opacity-100"
                      aria-label="Remove image"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))
              : liveRows.map((image) => (
                  <div key={image.id} className="group relative">
                    <MediaDropField
                      value={image.mediaAssetId}
                      onChange={() => undefined}
                      kind="image"
                      folder="packages"
                      variant="square"
                      disabled
                      label={image.altText ?? undefined}
                    />
                    <button
                      type="button"
                      onClick={() => removeLive(image.id)}
                      disabled={del.isPending}
                      className="absolute top-1 right-1 z-10 inline-flex h-7 w-7 items-center justify-center rounded-md bg-black/60 text-white opacity-0 transition-opacity group-hover:opacity-100 disabled:pointer-events-none disabled:opacity-50"
                      aria-label={`Remove image ${image.altText ?? ''}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
          </div>
        )}
      </div>
    </FormSection>
  );
};
